import { useMemo, useCallback } from "react";
import "./WizardCreation.css";

import WizardShell from "../../components/Wizard/WizardShell";
import BasicData from "../../pages/OfferingData/BasicData";
import Dataset from "../../pages/OfferingData/Dataset";
import Policy from "../../pages/OfferingData/Policy";
import Summary from "../../pages/OfferingData/Summary";

import { useWizardController } from "../../hooks/useWizardController";
import { validateStep } from "../../utils/validators";

import { useWorkerApiHelper } from "../../hooks/useWorkerApiHelper";
import { useAuthRoles } from "../../hooks/useAuthRoles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePostCatalog } from "../../hooks/catalogs/usePostCatalog";
import { useGetParticipantByParticipantId } from "../../hooks/participants/useGetParticipantByParticipantId";
import { getParticipantIdFromToken } from "../../pages/DataOfferings/Crud/offeringFormUtils";

export default function WizardCreation() {
  const steps = useMemo(
    () => [
      { key: "step1", Component: BasicData },
      { key: "step2", Component: Policy },
      { key: "step3", Component: Dataset },
      { key: "step4", Component: Summary },
    ],
    [],
  );

  const wizard = useWizardController(steps.length);
  const current = steps[wizard.state.stepIndex];
  const canGoNext = validateStep(current.key, wizard.state.data);

  const { callApiWithWorker } = useWorkerApiHelper();
  const { decoded } = useAuthRoles();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const participantIdFromToken = getParticipantIdFromToken(decoded);
  const { data: publisherParticipant, isLoading: isGettingPublisher } =
    useGetParticipantByParticipantId({
      callApiWithWorker,
      participantId: participantIdFromToken,
      enabled: !!participantIdFromToken,
    });

  const postCatalog = usePostCatalog({ callApiWithWorker, navigate, t });
  const publisherId = publisherParticipant?.id || "";
  const currentCanGoNext =
    current.key === "step4"
      ? canGoNext && Boolean(publisherId) && !isGettingPublisher
      : canGoNext;

  const uniq = (arr) => Array.from(new Set(arr));
  const toLangArray = useCallback(
    (value, lang = i18n.language || "en") => {
      if (Array.isArray(value)) {
        return value
          .map((entry) => ({
            language: entry?.language ?? entry?.lang ?? lang,
            value: entry?.value ?? entry?.text ?? "",
          }))
          .filter(
            (entry) =>
              typeof entry.language === "string" &&
              entry.language.trim() !== "" &&
              typeof entry.value === "string" &&
              entry.value.trim() !== "",
          );
      }

      return typeof value === "string" && value.trim()
        ? [{ language: lang, value: value.trim() }]
        : [];
    },
    [i18n.language],
  );

  const onPublish = useCallback(
    async (allData) => {
      const step1 = allData?.step1 ?? {};
      const step3 = allData?.step3 ?? {};

      const related = Array.isArray(step3?.selectedIris)
        ? step3.selectedIris
        : [];
      const related_resource = uniq(
        related.filter((v) => typeof v === "string" && v.trim() !== ""),
      );

      const payloadObj = {
        title: toLangArray(step1?.title?.length ? step1.title : step1?.name),
        description: toLangArray(
          step1?.description?.length
            ? step1.description
            : step1?.descriptionText,
        ),
        publisher: publisherId,
        related_resource,
      };

      return postCatalog.mutateAsync(payloadObj);
    },
    [postCatalog, publisherId, toLangArray],
  );

  return (
    <WizardShell
      activeIndex={wizard.state.stepIndex}
      onGoto={(idx) => {
        if (idx < wizard.state.stepIndex) wizard.goto(idx);
      }}
    >
      <current.Component
        stepKey={current.key}
        stepData={wizard.state.data[current.key]}
        allData={wizard.state.data}
        setStepData={wizard.setStepData}
        next={wizard.next}
        back={wizard.back}
        isFirst={wizard.state.stepIndex === 0}
        canGoNext={currentCanGoNext}
        reset={wizard.reset}
        isLast={wizard.state.stepIndex === steps.length - 1}
        onPublish={onPublish}
        isPosting={postCatalog.isPending || isGettingPublisher}
      />
    </WizardShell>
  );
}
